/**
 * Feedback Service
 * Handles bug reports, feature requests, improvements, and survey responses via Firestore.
 * All feedback types trigger a Cloud Function that creates GitHub Issues.
 * Survey responses are exported to Google Sheets via a separate Cloud Function.
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Gather device/browser metadata automatically
 */
function getDeviceMetadata() {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    currentUrl: window.location.href,
    currentRoute: window.location.pathname,
  };
}

/**
 * Submit feedback (bug, feature_request, or improvement) to Firestore.
 * The Cloud Function `onFeedbackCreate` will auto-create a GitHub Issue.
 */
export async function submitFeedback({ type, title, description, severity, stepsToReproduce, csat, user, betaStatus }) {
  const doc = {
    type,
    title,
    description,
    csat: csat || null,
    userId: user.uid,
    userEmail: user.email,
    displayName: user.displayName || '',
    betaStatus: betaStatus || 'unknown',
    device: getDeviceMetadata(),
    createdAt: serverTimestamp(),
    status: 'submitted',
  };

  if (type === 'bug') {
    doc.severity = severity || 'medium';
    doc.stepsToReproduce = stepsToReproduce || '';
  }

  const docRef = await addDoc(collection(db, 'feedback'), doc);
  return docRef.id;
}

/**
 * Get the currently active survey (if any).
 * Returns null if no active survey exists.
 */
export async function getActiveSurvey() {
  const q = query(
    collection(db, 'surveys'),
    where('active', '==', true)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() };
}

/**
 * Check if the user has already completed a specific survey.
 */
export async function hasCompletedSurvey(userId, surveyId) {
  const q = query(
    collection(db, 'surveyResponses'),
    where('userId', '==', userId),
    where('surveyId', '==', surveyId)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Submit a survey response.
 */
export async function submitSurveyResponse({ surveyId, answers, user, betaStatus }) {
  const docRef = await addDoc(collection(db, 'surveyResponses'), {
    surveyId,
    answers,
    userId: user.uid,
    userEmail: user.email,
    betaStatus: betaStatus || 'unknown',
    createdAt: serverTimestamp(),
  });
  return docRef.id;
}
